'use client';

import { useGetAllBotsData } from '@/hooks/useBot';
import * as React from 'react';
import { useUserContext } from './UserProvider';
import { useWorkspace } from './WorkspaceProvider';

interface IBotProviderProps {
  children: React.ReactNode;
}

interface IBotContext {
  goLive: boolean;
  setGoLive: React.Dispatch<React.SetStateAction<boolean>>;
  botOptions: any[];
}

const BotContext = React.createContext<IBotContext>({} as IBotContext);

export const useBotContext = () => {
  const context = React.useContext(BotContext);
  if (!context) {
    throw new Error('BotContext must be used within provider!');
  }
  return context;
};

const BotProvider: React.FunctionComponent<IBotProviderProps> = (props) => {
  const { vendorConfig } = useUserContext();
  const workspaceContext = useWorkspace();

  // Use active workspace ID if available, otherwise fall back to vendorConfig
  const vendorId = workspaceContext?.activeWorkspace?.vendorid || vendorConfig?.vendorId;
  const { data: allBotsData } = useGetAllBotsData(vendorId as number);

  const [goLive, setGoLive] = React.useState<boolean>(false);
  const [botOptions, setBotOptions] = React.useState<any[]>([]);

  // Memoize bot options processing to prevent unnecessary recalculations
  React.useEffect(() => {
    if (allBotsData && Array.isArray(allBotsData)) {
      const activeBots = allBotsData
        .filter((item) => item.status === true)
        .map((item) => ({
          label: item.customizationdata?.name,
          value: item,
        }));

      setBotOptions(activeBots);
    } else {
      // Clear bot options when workspace changes and no bots are available
      setBotOptions([]);
    }
  }, [allBotsData, vendorId]); // Add vendorId as dependency to re-fetch when workspace changes

  return (
    <BotContext.Provider
      value={{
        goLive,
        setGoLive,
        botOptions,
      }}
    >
      {props.children}
    </BotContext.Provider>
  );
};

export default BotProvider;
